import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/ui";
import { useAuth } from "@/hooks/auth";
import { useParams } from "react-router-dom";

interface RealStudent {
  id: string;
  school_id: string;
  student_id: string;
  name: string;
  grade: string;
  section: string;
  phone?: string;
  parent_phone?: string;
  address?: string;
  birth_date?: string;
  enrollment_date?: string;
  status: "active" | "inactive" | "transferred";
}

/** PostgREST returns at most one page per request; paginate to load every active student. */
const STUDENTS_PAGE_SIZE = 1000;

function mapRowToRealStudent(student: Record<string, unknown>): RealStudent {
  return {
    ...student,
    status: student.status as RealStudent["status"],
  } as RealStudent;
}

function compareStudentsByName(a: RealStudent, b: RealStudent): number {
  return a.name.localeCompare(b.name, "ar", { sensitivity: "base" });
}

/**
 * Hard-delete many students and dependent rows in bulk (few round-trips).
 * Deletes `profiles` rows linked via `student_id`.
 */
export async function permanentlyDeleteStudentsFromSchool(
  schoolId: string,
  studentUuids: string[]
): Promise<void> {
  const ids = [...new Set(studentUuids.filter(Boolean))];
  if (ids.length === 0) return;

  const [att, late, notif, progress, prof] = await Promise.all([
    supabase
      .from("attendance_records")
      .delete()
      .in("student_id", ids)
      .eq("school_id", schoolId),
    supabase
      .from("late_attendance_records")
      .delete()
      .in("student_id", ids)
      .eq("school_id", schoolId),
    supabase
      .from("notifications_log")
      .delete()
      .in("student_id", ids),
    supabase
      .from("student_plan_progress")
      .delete()
      .in("student_id", ids),
    supabase.from("profiles").delete().in("student_id", ids),
  ]);

  for (const res of [att, late, notif, progress, prof]) {
    if (res.error) throw res.error;
  }

  const { data: deletedRows, error: delErr } = await supabase
    .from("students")
    .delete()
    .in("id", ids)
    .eq("school_id", schoolId)
    .select("id");

  if (delErr) throw delErr;
  const n = deletedRows?.length ?? 0;
  if (n !== ids.length) {
    throw new Error(
      `تعذر حذف كل الطلاب المطلوبين (${n} من أصل ${ids.length})`
    );
  }
}

/** Hard-delete one student (delegates to bulk). */
export async function permanentlyDeleteStudentFromSchool(
  schoolId: string,
  studentUuid: string
): Promise<void> {
  await permanentlyDeleteStudentsFromSchool(schoolId, [studentUuid]);
}

// ✅ Query function (fetch all students for the school)
const DELETE_ALL_CHUNK = 100;

/** Every student row for the school (any status), for wipe / admin actions. */
async function fetchAllStudentIdsForSchool(schoolId: string): Promise<string[]> {
  const ids: string[] = [];
  let from = 0;

  for (;;) {
    const to = from + STUDENTS_PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from("students")
      .select("id")
      .eq("school_id", schoolId)
      .order("id", { ascending: true })
      .range(from, to);

    if (error) throw error;
    const batch = data ?? [];
    ids.push(...batch.map((r) => (r as { id: string }).id));
    if (batch.length < STUDENTS_PAGE_SIZE) break;
    from += STUDENTS_PAGE_SIZE;
  }

  return ids;
}

async function fetchStudents(schoolId: string): Promise<RealStudent[]> {
  const all: RealStudent[] = [];
  let from = 0;

  for (;;) {
    const to = from + STUDENTS_PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from("students")
      .select("*")
      .eq("school_id", schoolId)
      .eq("status", "active")
      .order("name", { ascending: true })
      .order("id", { ascending: true })
      .range(from, to);

    if (error) throw error;

    const batch = data || [];
    all.push(...batch.map(mapRowToRealStudent));

    if (batch.length < STUDENTS_PAGE_SIZE) break;
    from += STUDENTS_PAGE_SIZE;
  }

  return all;
}

async function invalidateStudentRelatedQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  schoolId: string | undefined
) {
  await queryClient.invalidateQueries({
    queryKey: ["students", schoolId],
    refetchType: "active",
  });
  await queryClient.invalidateQueries({
    queryKey: ["attendance"],
    refetchType: "active",
  });
  await queryClient.invalidateQueries({
    queryKey: ["late_records"],
    refetchType: "active",
  });
}

export const useRealStudents = () => {
  const { toast } = useToast();
  const { user, userProfile } = useAuth();
  const { schoolId: schoolIdParam } = useParams();
  const schoolId = schoolIdParam || userProfile?.school_id || user?.id;
  const queryClient = useQueryClient();

  // ✅ Fetch students with React Query
  const {
    data: students = [],
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: ["students", schoolId],
    queryFn: () => fetchStudents(schoolId),
    enabled: !!schoolId, // run only when schoolId is available
    staleTime: 1000 * 60 * 2, // cache for 2 minutes
    retry: 1, // avoid too many retries
  });

  // Show toast on error
  if (error) {
    toast({
      title: "تعذر تحميل الطلاب",
      description: "يرجى التحقق من الاتصال والمحاولة مرة أخرى",
      variant: "destructive",
    });
  }

  // ✅ Mutation: Add student
  const addStudentMutation = useMutation({
    mutationFn: async (studentData: Omit<RealStudent, "id" | "school_id">) => {
      const { data, error } = await supabase
        .from("students")
        .insert([{ ...studentData, school_id: schoolId }])
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        status: data.status as "active" | "inactive" | "transferred",
      };
    },
    onSuccess: (newStudent) => {
      queryClient.setQueryData<RealStudent[]>(
        ["students", schoolId],
        (old = []) => {
          const next = [...old, newStudent];
          next.sort(compareStudentsByName);
          return next;
        }
      );

      toast({
        title: "تم إضافة الطالب",
        description: `تم إضافة ${newStudent.name} بنجاح`,
      });
    },
    onError: () =>
      toast({
        title: "خطأ في الإضافة",
        description: "لم نتمكن من إضافة الطالب",
        variant: "destructive",
      }),
  });

  // ✅ Mutation: Update student
  const updateStudentMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<RealStudent>;
    }) => {
      const { data, error } = await supabase
        .from("students")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        status: data.status as "active" | "inactive" | "transferred",
      };
    },
    onSuccess: (updatedStudent) => {
      queryClient.setQueryData<RealStudent[]>(
        ["students", schoolId],
        (old = []) => {
          const next = old.map((s) =>
            s.id === updatedStudent.id ? updatedStudent : s
          );
          next.sort(compareStudentsByName);
          return next;
        }
      );

      toast({
        title: "تم التحديث",
        description: "تم تحديث بيانات الطالب بنجاح",
      });
    },
    onError: () =>
      toast({
        title: "خطأ في التحديث",
        description: "لم نتمكن من تحديث بيانات الطالب",
        variant: "destructive",
      }),
  });

  // ✅ Mutation: Delete one student permanently
  const deleteStudentMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!schoolId) {
        throw new Error("Missing school context");
      }
      await permanentlyDeleteStudentsFromSchool(schoolId, [id]);
      return id;
    },
    onSuccess: async (id) => {
      queryClient.setQueryData<RealStudent[]>(
        ["students", schoolId],
        (old = []) => old.filter((s) => s.id !== id)
      );

      await invalidateStudentRelatedQueries(queryClient, schoolId);

      toast({
        title: "تم الحذف",
        description: "تم حذف الطالب نهائياً من النظام",
      });
    },
    onError: () =>
      toast({
        title: "خطأ في الحذف",
        description: "لم نتمكن من حذف الطالب",
        variant: "destructive",
      }),
  });

  // ✅ Mutation: Delete many students permanently (single bulk per table)
  const deleteStudentsMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!schoolId) {
        throw new Error("Missing school context");
      }
      const unique = [...new Set(ids.filter(Boolean))];
      if (unique.length === 0) return [];
      await permanentlyDeleteStudentsFromSchool(schoolId, unique);
      return unique;
    },
    onSuccess: async (deletedIds) => {
      if (deletedIds.length === 0) return;
      const idSet = new Set(deletedIds);
      queryClient.setQueryData<RealStudent[]>(
        ["students", schoolId],
        (old = []) => old.filter((s) => !idSet.has(s.id))
      );

      await invalidateStudentRelatedQueries(queryClient, schoolId);

      toast({
        title: "تم الحذف",
        description:
          deletedIds.length === 1
            ? "تم حذف الطالب نهائياً من النظام"
            : `تم حذف ${deletedIds.length} طالباً نهائياً من النظام`,
      });
    },
    onError: () =>
      toast({
        title: "خطأ في الحذف",
        description: "لم نتمكن من إكمال حذف الطلاب المحددين",
        variant: "destructive",
      }),
  });

  /** Permanently delete every student row for this school (all statuses). */
  const deleteAllStudentsMutation = useMutation({
    mutationFn: async () => {
      if (!schoolId) {
        throw new Error("Missing school context");
      }
      const ids = await fetchAllStudentIdsForSchool(schoolId);
      if (ids.length === 0) return 0;
      for (let i = 0; i < ids.length; i += DELETE_ALL_CHUNK) {
        const chunk = ids.slice(i, i + DELETE_ALL_CHUNK);
        await permanentlyDeleteStudentsFromSchool(schoolId, chunk);
      }
      return ids.length;
    },
    onSuccess: async (removedCount) => {
      queryClient.setQueryData<RealStudent[]>(["students", schoolId], []);
      await invalidateStudentRelatedQueries(queryClient, schoolId);
      toast({
        title: "تم الحذف",
        description:
          removedCount === 0
            ? "لا يوجد طلاب لحذفهم"
            : `تم حذف ${removedCount} طالباً نهائياً من المدرسة`,
      });
    },
    onError: () =>
      toast({
        title: "خطأ في الحذف",
        description: "لم نتمكن من حذف جميع الطلاب",
        variant: "destructive",
      }),
  });

  return {
    schoolId: schoolId ?? null,
    students,
    loading: isLoading,
    fetchingData: isFetching,
    addStudent: addStudentMutation.mutateAsync,
    updateStudent: (id: string, updates: Partial<RealStudent>) =>
      updateStudentMutation.mutateAsync({ id, updates }),
    deleteStudent: deleteStudentMutation.mutateAsync,
    deleteStudents: deleteStudentsMutation.mutateAsync,
    deleteAllStudents: deleteAllStudentsMutation.mutateAsync,
    deletingStudents:
      deleteStudentMutation.isPending ||
      deleteStudentsMutation.isPending ||
      deleteAllStudentsMutation.isPending,
    refetch,
  };
};
